// 3rd party dependencies
import { useState, useEffect } from 'react';
import { Navigate, createBrowserRouter, redirect } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// internal dependencies
import UDM from './features/UDM/UDM';
import Marketplace, {
	clientLoader as marketplaceLoader,
} from './features/Marketplace/Marketplace';
import UserList, { loader as usersLoader } from './features/Users/UserList';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import Root, { rootLoader } from './components/Root';
import theme from './styles/theme';
import darkTheme from './styles/darkTheme';
import ErrorPage from './components/ErrorPage';
import QSEmbeddedDashboard from './features/AnswersEmbeddedAuthoring/QuicksightEmbeddedAuthoring';
import DataTables, {
	loader as dataTablesLoader,
} from './features/DataTables/DataTables';
import DataIndex from './features/DataTables/DataIndex';
import DataTable, {
	loader as dataTableLoader,
} from './features/DataTables/DataTable';
import DataTableRecords, {
	loader as dataTableRecordsLoader,
} from './features/DataTables/DataTableRecords';
import DataTableSettings, {
	action as dataTableSettingsAction,
} from './features/DataTables/DataTableSettings';
import DataTableIndex from './features/DataTables/DataTableIndex';
import DataTableNew, {
	action as dataTableNewAction,
} from './features/DataTables/DataTableNew';
import {
	approvalAction,
	recordUpdateAction,
	getPresignedUrlAction,
} from './features/DataTables/DataGridDrawer';
import Connections, {
	action as createConnectionAction,
} from './features/Connections/Connections';
// import ConnectionEdit, { updateAction as connectionUpdateAction, loader as connectionEditLoader } from './features/Connections/ConnectionEdit';
import ConnectionsList, {
	loader as connectionsListLoader,
} from './features/Connections/ConnectionsList';
import ConnectionNew from './features/Connections/ConnectionNew';
import Datalake, {
	loader as datalakeLoader,
	action as createDatalakeAction,
} from './features/Datalake/Datalake';
import DatalakeIndex from './features/Datalake/DatalakeIndex';
import DatalakeDetails, {
	action as datalakeUpdateAction,
} from './features/Datalake/DatalakeDetails';
import AscentFiles, {
	clientLoader as ascentFilesLoader,
} from './features/Ascent/AscentFiles';
import AscentEmail, {
	clientLoader as ascentEmailLoader,
	action as ascentEmailAction,
} from './features/Ascent/AscentEmail';
import { action as ascentEmailCancelAction } from './features/Ascent/AscentEmailCancel';
import { action as ascentEmailUpdateAction } from './features/Ascent/AscentEmailUpdate';
import Tenant, { tenantLoader } from './components/Tenant';
import RootIndex from './components/RootIndex';
import Pricing from './features/Pricing/Pricing';
import TenantAdmin from './features/TenantAdmin/TenantAdmin';
import EmailSyncAdmin, {
	clientLoader as emailSyncAdminLoader,
} from './features/EmailSyncAdmin/EmailSyncAdmin';
import SalesforceLogin, {
	loader as salesforceLoginLoader,
} from './features/SalesforceLogin/SalesforceLogin';
import SSOHandler, {
	loader as ssoLoader,
} from './features/SalesforceLogin/SSOHandler';
import AimeConfig, {
	ErrorBoundary as AimeConfigErrorBoundary,
	clientLoader as aimeConfigLoader,
} from './features/AIMe/AimeConfig';
import {
	PipelinePage,
	DealsPage,
	MonitorsPage,
	PromptsPage,
	SettingsPage,
} from './features/Investing';
import { loader as pipelineLoader } from './features/Investing/pages/PipelinePage';
import { loader as dealsLoader } from './features/Investing/pages/DealsPage';
import { loader as promptsLoader } from './features/Investing/pages/PromptsPage';
import DealDetail, {
	dealDetailLoader,
} from './features/Investing/pages/DealDetail';
import EmailDataTables, {
	clientLoader as emailDataTablesLoader,
} from './features/Ascent/EmailDataTables';
import { action as ascentEmailSyncAction } from './features/Ascent/AscentEmailSync';
import AimeThreadRedirect from './features/AimeRedirect/AimeThreadRedirect';
import IRWorkstationLayout from './features/IRWorkstation/components/Layout/IRWorkstationLayout';
import Overview from './features/IRWorkstation/pages/Overview';
import EntityDashboard from './features/IRWorkstation/pages/EntityDashboard';
import EntityDetail from './features/IRWorkstation/pages/EntityDetail';
import CoInvestDashboard from './features/IRWorkstation/pages/CoInvestDashboard';
import AlertCenter from './features/IRWorkstation/pages/AlertCenter';
import SelectReportForAlert from './features/IRWorkstation/pages/SelectReportForAlert';
import GenerateAlertFromReport from './features/IRWorkstation/pages/GenerateAlertFromReport';

// higher-order function used for consistent login redirect logic across loaders
import { loaderWrapper, authedApiFetch } from './utils/Api';
import { createLoaderDebug } from './loaderDebug';
import { ThemeModeContext } from './contexts/ThemeModeContext';

import { NotificationContext } from 'contexts/NotificationContext';
import { useNotification } from 'hooks/useNotification';
import Notification from 'components/Notification';

const wrapLoader = (loader, name) => {
	const debugLoader =
		process.env.NODE_ENV === 'development'
			? createLoaderDebug(loader, name)
			: loader;
	return loaderWrapper(debugLoader);
};

const App = () => {
	// Step 2: Theme mode state with persistence
	const getInitialMode = () => {
		if (typeof window !== 'undefined') {
			return localStorage.getItem('themeMode') || 'light';
		}
		return 'light';
	};
	const [mode, setMode] = useState(getInitialMode);
	useEffect(() => {
		localStorage.setItem('themeMode', mode);
	}, [mode]);

	// Step 3: Theme selection
	const currentTheme = mode === 'dark' ? darkTheme : theme;

	// Handler for UserMenu toggle
	const handleToggleTheme = () => {
		setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
	};

	const themeModeContextValue = {
		mode,
		toggleTheme: handleToggleTheme,
	};

	const { notification, showNotification, hideNotification } =
		useNotification();

	const router = createBrowserRouter([
		{
			path: '/login',
			element: <Login />,
		},
		{
			path: '/auth/callback',
			element: <AuthCallback />,
		},
		{
			path: '/sf/login',
			loader: salesforceLoginLoader,
			element: <SalesforceLogin />,
		},
		{
			path: '/sf/sso/:provider',
			loader: ssoLoader,
			element: <SSOHandler />,
		},
		{
			path: '/p/aime/thread/:threadId',
			element: <AimeThreadRedirect />,
		},
		{
			path: '/',
			element: <Root />,
			loader: wrapLoader(rootLoader, 'rootLoader'),
			errorElement: <ErrorPage />,
			children: [
				{
					index: true,
					element: <RootIndex />,
				},
				{
					path: 'tenants/:tenantId',
					element: <Tenant />,
					loader: wrapLoader(tenantLoader, 'tenantLoader'),
					children: [
						{
							index: true,
							path: 'marketplace',
							element: <Marketplace />,
							loader: wrapLoader(marketplaceLoader, 'marketplaceLoader'),
						},
						{
							path: 'pricing',
							element: <Pricing />,
						},
						{
							path: 'udm',
							element: <UDM />,
						},
						{
							path: 'email-sync',
							children: [
								{
									index: true,
									element: <AscentEmail />,
									loader: wrapLoader(ascentEmailLoader, 'ascentEmailLoader'),
									action: wrapLoader(ascentEmailAction, 'ascentEmailAction'),
								},
								{
									path: ':jobId/records',
									loader: wrapLoader(
										emailDataTablesLoader,
										'emailDataTablesLoader'
									),
									element: <EmailDataTables />,
									children: [
										{
											path: 'update',
											action: wrapLoader(
												ascentEmailUpdateAction,
												'ascentEmailUpdateAction'
											),
										},
									],
								},
								{
									path: ':jobId/sync',
									action: wrapLoader(
										ascentEmailSyncAction,
										'ascentEmailSyncAction'
									),
								},
								{
									path: ':jobId/cancel',
									action: wrapLoader(
										ascentEmailCancelAction,
										'ascentEmailCancelAction'
									),
								},
							],
						},
						{
							path: 'file-upload',
							element: <AscentFiles />,
							loader: wrapLoader(ascentFilesLoader, 'ascentFilesLoader'),
						},
						{
							path: 'users',
							element: <UserList />,
							loader: wrapLoader(usersLoader, 'usersLoader'),
						},
						{
							path: 'answersDashboard',
							element: <QSEmbeddedDashboard />,
						},
						{
							path: 'datalake',
							element: <Datalake />,
							action: createDatalakeAction,
							loader: wrapLoader(datalakeLoader, 'datalakeLoader'),
							errorElement: <ErrorPage />,
							children: [
								{
									index: true,
									element: <DatalakeIndex />,
									loader: wrapLoader(datalakeLoader, 'datalakeLoader'),
								},
								{
									path: ':connectionId',
									element: <DatalakeDetails />,
									action: wrapLoader(
										datalakeUpdateAction,
										'datalakeUpdateAction'
									),
								},
							],
						},
						{
							path: 'connections',
							element: <Connections />,
							errorElement: <ErrorPage />,
							children: [
								{
									index: true,
									element: <ConnectionsList />,
									loader: wrapLoader(
										connectionsListLoader,
										'connectionsListLoader'
									),
								},
								{
									path: 'new',
									element: <ConnectionNew />,
									action: createConnectionAction,
								},
								// {
								//   path: ":connectionId",
								//   element: <ConnectionEdit />,
								//   loader: wrapLoader(connectionEditLoader, 'connectionEditLoader'),
								//   action: connectionUpdateAction
								// }
							],
						},
						{
							path: 'data-tables',
							element: <DataTables />,
							loader: wrapLoader(dataTablesLoader, 'dataTablesLoader'),
							errorElement: <ErrorPage />,
							children: [
								{
									index: true,
									element: <DataIndex />,
								},
								{
									path: 'new',
									element: <DataTableNew />,
									action: dataTableNewAction,
								},
								{
									path: ':dataTableId',
									element: <DataTable />,
									loader: wrapLoader(dataTableLoader, 'dataTableLoader'),
									children: [
										{
											index: true,
											element: <DataTableIndex />,
										},
										{
											path: 'records',
											element: <DataTableRecords />,
											loader: wrapLoader(
												dataTableRecordsLoader,
												'dataTableRecordsLoader'
											),
											children: [
												{
													path: ':recordId/update',
													action: recordUpdateAction,
												},
												{
													path: ':recordId/approve',
													action: approvalAction,
												},
												{
													path: ':recordId/presigned_url',
													action: getPresignedUrlAction,
												},
											],
										},
										{
											path: 'settings',
											element: <DataTableSettings />,
											action: dataTableSettingsAction,
										},
									],
								},
							],
						},
						{
							path: 'admin',
							element: <TenantAdmin />,
						},
						{
							path: 'email-sync-admin',
							children: [
								{
									index: true,
									element: <EmailSyncAdmin />,
									loader: wrapLoader(
										emailSyncAdminLoader,
										'emailSyncAdminLoader'
									),
								},
								{
									path: ':jobId/records',
									element: <EmailDataTables />,
									loader: wrapLoader(
										emailDataTablesLoader,
										'emailDataTablesLoader'
									),
								},
							],
						},
						// Consider putting this on or below the admin route above
						{
							path: 'aime-config',
							loader: wrapLoader(aimeConfigLoader, 'aimeConfigLoader'),
							element: <AimeConfig />,
							errorElement: <AimeConfigErrorBoundary />,
						},
						{
							path: 'apps/investing',
							element: <Navigate to='pipeline' />,
						},
						{
							path: 'apps/investing/pipeline',
							element: <PipelinePage />,
							loader: wrapLoader(pipelineLoader, 'pipelineLoader'),
						},
						{
							path: 'apps/investing/deals',
							element: <DealsPage />,
							loader: wrapLoader(dealsLoader, 'dealsLoader'),
						},
						{
							path: 'apps/investing/deals/:dealId',
							element: <DealDetail />,
							loader: wrapLoader(dealDetailLoader, 'dealDetailLoader'),
						},
						{
							path: 'apps/investing/monitors',
							element: <MonitorsPage />,
						},
						{
							path: 'apps/investing/prompts',
							children: [
								{
									index: true,
									element: <PromptsPage />,
									loader: wrapLoader(promptsLoader, 'promptsLoader'),
								},
								{
									path: 'new',
									element: <PromptsPage />,
									loader: wrapLoader(promptsLoader, 'promptsLoader'),
								},
								{
									path: ':promptId',
									element: <PromptsPage />,
									loader: wrapLoader(promptsLoader, 'promptsLoader'),
								},
							],
						},
						{
							path: 'apps/investing/settings',
							element: <SettingsPage />,
						},
					],
				},
			],
		},
		{
			// the IR Workstation lives outside of the regular Launchpad UI, so it's a top-level route
			// Any changes to this base path name 'workstation' should also be reflected in:
			// - the useDocumentTitle hook
			// - public/index.html 
			// to ensure the browser tab title is set correctly
			path: '/tenants/:tenantId/workstation',
			element: <IRWorkstationLayout />,
			loader: wrapLoader(async ({ params }) => {
				const { tenantId } = params;
				const flagParams = new URLSearchParams();
				flagParams.append('tenant_id', tenantId);
				flagParams.append('flag_keys[]', 'enable-ir-workstation');
				const res = await authedApiFetch({
					endpoint: `/launchdarkly_flags?${flagParams.toString()}`,
					method: 'GET',
				});
				if (!res?.statuses?.['enable-ir-workstation']) {
					return redirect('/');
				}
				return null;
			}, 'workstationLoader'),
			errorElement: <ErrorPage />,
			children: [
				{
					index: true,
					element: <Overview />,
				},
				{
					path: 'entities',
					element: <EntityDashboard />,
				},
				{
					path: 'entities/:entityId',
					element: <EntityDetail />,
				},
				{
					path: 'co-invests',
					element: <CoInvestDashboard />,
				},
				{
					path: 'alert_center',
					children: [
						{
							index: true,
							element: <AlertCenter />,
						},
						{
							path: 'new',
							element: <SelectReportForAlert />,
						},
						{
							path: 'new/analysis/:reportId',
							element: <GenerateAlertFromReport />,
						},
					],
				},
			],
		},
		{
			path: '*',
			element: <Navigate to='/' />,
		},
	]);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<ThemeModeContext.Provider value={themeModeContextValue}>
				<ThemeProvider theme={currentTheme}>
					<CssBaseline />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							height: '100vh',
							width: '100vw',
							position: 'relative',
						}}
					>
						<div
							style={{
								flex: 1,
								minWidth: 0,
								height: '100vh',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							{/* Expose notification actions to the entire app via context. */}
							<NotificationContext.Provider value={{ showNotification }}>
								<RouterProvider router={router} />
							</NotificationContext.Provider>

							{/* Root-level toast renderer that displays notifications triggered via context. */}
							<Notification
								notification={notification}
								hideNotification={hideNotification}
							/>
						</div>
					</Box>
				</ThemeProvider>
			</ThemeModeContext.Provider>
		</LocalizationProvider>
	);
};

export default App;
